<template>
    <div class="page">
        <div class="page-header">
            <div>
                <h2 class="page-title">{{ t('registration_queue') }}</h2>
                <p class="page-subtitle">{{ t('registration_queue_subtitle') }}</p>
            </div>
        </div>

        <div class="card">
            <div class="datatable-toolbar">
                <div class="toolbar-left">
                    <select v-model.number="perPage" class="length-select" :disabled="loading">
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>

                    <select v-model="statusFilter" class="status-select" :disabled="loading">
                        <option value="pending">{{ t('pending') }}</option>
                        <option value="processed">{{ t('processed') }}</option>
                    </select>

                    <button type="button" class="tool-btn tool-btn-icon" @click="toggleFilters" :disabled="loading">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M2.5 4.16667H17.5M5.83333 10H14.1667M8.33333 15.8333H11.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>

                    <div class="columns-wrap">
                        <button type="button" class="tool-btn tool-btn-light" @click="toggleColumns" :disabled="loading">COLUMNS</button>
                        <div v-if="showColumns" class="columns-dropdown">
                            <label v-for="c in columns" :key="c.key" class="columns-item">
                                <input type="checkbox" v-model="visibleColumns[c.key]" :disabled="c.locked">
                                <span>{{ t(c.labelKey) }}</span>
                            </label>
                        </div>
                    </div>

                    <button type="button" class="tool-btn tool-btn-primary" @click="load" :disabled="loading">{{ t('refresh') }}</button>
                    <button type="button" class="tool-btn tool-btn-light" @click="clearFilters" :disabled="!hasAnyFilter">{{ t('clear_filters') }}</button>
                </div>

                <div class="toolbar-right">
                    <div class="search-box">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <input v-model="searchQuery" type="text" class="search-input" :placeholder="t('search') + '...'" @input="debouncedSearch" :disabled="loading">
                    </div>
                </div>
            </div>

            <div class="table-container">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th v-if="visibleColumns.id">
                                <input v-if="showFilters" v-model="colFilters.id" type="text" class="th-input" :placeholder="t('id')">
                                <span v-else>{{ t('id') }}</span>
                            </th>
                            <th v-if="visibleColumns.date">
                                <input v-if="showFilters" v-model="colFilters.date" type="text" class="th-input" :placeholder="t('date')">
                                <span v-else>{{ t('date') }}</span>
                            </th>
                            <th v-if="visibleColumns.member_type">
                                <input v-if="showFilters" v-model="colFilters.member_type" type="text" class="th-input" :placeholder="t('member_type')">
                                <span v-else>{{ t('member_type') }}</span>
                            </th>
                            <th v-if="visibleColumns.category">
                                <input v-if="showFilters" v-model="colFilters.category" type="text" class="th-input" :placeholder="t('category')">
                                <span v-else>{{ t('category') }}</span>
                            </th>
                            <th v-if="visibleColumns.customer_name">
                                <input v-if="showFilters" v-model="colFilters.customer_name" type="text" class="th-input" :placeholder="t('customer_name')">
                                <span v-else>{{ t('customer_name') }}</span>
                            </th>
                            <th v-if="visibleColumns.mobile">
                                <input v-if="showFilters" v-model="colFilters.mobile" type="text" class="th-input" :placeholder="t('mobile')">
                                <span v-else>{{ t('mobile') }}</span>
                            </th>
                            <th v-if="visibleColumns.email">
                                <input v-if="showFilters" v-model="colFilters.email" type="text" class="th-input" :placeholder="t('email')">
                                <span v-else>{{ t('email') }}</span>
                            </th>
                            <th v-if="visibleColumns.district">
                                <input v-if="showFilters" v-model="colFilters.district" type="text" class="th-input" :placeholder="t('district')">
                                <span v-else>{{ t('district') }}</span>
                            </th>
                            <th v-if="visibleColumns.status">
                                <select v-if="showFilters" v-model="colFilters.status" class="th-select">
                                    <option value="">{{ t('status') }}</option>
                                    <option value="pending">{{ t('pending') }}</option>
                                    <option value="processed">{{ t('processed') }}</option>
                                </select>
                                <span v-else>{{ t('status') }}</span>
                            </th>
                            <th v-if="visibleColumns.actions">{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td :colspan="visibleColumnCount" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="row in filteredItems" v-if="!loading" :key="row.RegistrationQueueID">
                            <td v-if="visibleColumns.id" class="mono">{{ row.RegistrationQueueID }}</td>
                            <td v-if="visibleColumns.date">{{ formatDateTime(row.RegistrationDate) }}</td>
                            <td v-if="visibleColumns.member_type">{{ row.MemberType || '-' }}</td>
                            <td v-if="visibleColumns.category">{{ row.MembershipCategory || '-' }}</td>
                            <td v-if="visibleColumns.customer_name">{{ row.CustomerName || '-' }}</td>
                            <td v-if="visibleColumns.mobile" class="mono">{{ row.MobileNo || '-' }}</td>
                            <td v-if="visibleColumns.email">{{ row.Email || '-' }}</td>
                            <td v-if="visibleColumns.district">{{ row.District || '-' }}</td>
                            <td v-if="visibleColumns.status">
                                <span :class="['tag', row.Status ? 'tag-warning' : 'tag-gray']">
                                    {{ row.Status ? t('pending') : t('processed') }}
                                </span>
                            </td>
                            <td v-if="visibleColumns.actions">
                                <div class="action-buttons">
                                    <button
                                        v-if="row.Status"
                                        type="button"
                                        @click="accept(row.RegistrationQueueID)"
                                        class="tool-btn tool-btn-success"
                                        :disabled="busyIds[row.RegistrationQueueID] === true"
                                    >
                                        {{ t('accept') }}
                                    </button>
                                    <span v-else class="no-access-text">-</span>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="!loading && filteredItems.length === 0">
                            <td :colspan="visibleColumnCount" class="empty">{{ t('no_data') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pager">
                <div class="pager-left">{{ showingText }}</div>
                <div class="pager-right">
                    <button class="pager-btn" @click="goFirst" :disabled="loading || page <= 1">{{ t('first') }}</button>
                    <button class="pager-btn" @click="prevPage" :disabled="loading || page <= 1">{{ t('previous') }}</button>
                    <button v-for="p in pageButtons" :key="p" class="pager-num" :class="{ active: p === page }" @click="goPage(p)" :disabled="loading">{{ p }}</button>
                    <button class="pager-btn" @click="nextPage" :disabled="loading || page >= totalPages">{{ t('next') }}</button>
                    <button class="pager-btn" @click="goLast" :disabled="loading || page >= totalPages">{{ t('last') }}</button>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <div class="toast-container">
                <TransitionGroup name="toast" tag="div">
                    <div v-for="toast in toasts" :key="toast.id" :class="['toast', `toast-${toast.type}`]">
                        <div class="toast-content">
                            <p class="toast-message">{{ toast.message }}</p>
                        </div>
                        <button @click="removeToast(toast.id)" class="toast-close">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </TransitionGroup>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { computed, inject, onMounted, ref } from 'vue';

export default {
    name: 'RegistrationQueuePage',
    setup() {
        const adminT = inject('adminT', (k) => k);
        const adminLang = inject('adminLang', ref('en'));

        const loading = ref(true);
        const statusFilter = ref('pending');
        const searchQuery = ref('');
        const page = ref(1);
        const perPage = ref(20);
        const items = ref([]);
        const pagination = ref({ page: 1, per_page: 20, total: 0 });
        const busyIds = ref({});
        const toasts = ref([]);
        let searchTimer = null;
        const showFilters = ref(true);
        const showColumns = ref(false);
        const columns = [
            { key: 'id', labelKey: 'id', locked: false },
            { key: 'date', labelKey: 'date', locked: false },
            { key: 'member_type', labelKey: 'member_type', locked: false },
            { key: 'category', labelKey: 'category', locked: false },
            { key: 'customer_name', labelKey: 'customer_name', locked: false },
            { key: 'mobile', labelKey: 'mobile', locked: false },
            { key: 'email', labelKey: 'email', locked: false },
            { key: 'district', labelKey: 'district', locked: false },
            { key: 'status', labelKey: 'status', locked: false },
            { key: 'actions', labelKey: 'actions', locked: true },
        ];
        const visibleColumns = ref({
            id: true,
            date: true,
            member_type: true,
            category: true,
            customer_name: true,
            mobile: true,
            email: true,
            district: true,
            status: true,
            actions: true,
        });
        const colFilters = ref({
            id: '',
            date: '',
            member_type: '',
            category: '',
            customer_name: '',
            mobile: '',
            email: '',
            district: '',
            status: '',
        });

        const t = (key) => adminT(key);
        const langValue = () => (adminLang?.value === 'bn' ? 'bn' : 'en');

        const toggleFilters = () => {
            showFilters.value = !showFilters.value;
        };

        const toggleColumns = () => {
            showColumns.value = !showColumns.value;
        };

        const visibleColumnCount = computed(() => {
            const v = visibleColumns.value || {};
            return columns.reduce((sum, c) => sum + (v[c.key] ? 1 : 0), 0) || 1;
        });

        const hasAnyFilter = computed(() => {
            if ((searchQuery.value || '').trim() !== '') return true;
            if ((statusFilter.value || 'pending') !== 'pending') return true;
            const f = colFilters.value || {};
            return Object.values(f).some((x) => String(x || '').trim() !== '');
        });

        const totalPages = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            return Math.max(1, Math.ceil(total / Math.max(1, size)));
        });

        const pageButtons = computed(() => {
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const total = totalPages.value;
            const maxBtns = 5;
            let start = Math.max(1, current - Math.floor(maxBtns / 2));
            let end = Math.min(total, start + maxBtns - 1);
            start = Math.max(1, end - maxBtns + 1);
            const arr = [];
            for (let i = start; i <= end; i++) arr.push(i);
            return arr;
        });

        const showingText = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || 20);
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const from = total === 0 ? 0 : (current - 1) * size + 1;
            const to = total === 0 ? 0 : Math.min(current * size, total);
            const tpl = t('showing_entries');
            const base = tpl && tpl !== 'showing_entries' && tpl.includes('{from}')
                ? tpl.replace('{from}', String(from)).replace('{to}', String(to)).replace('{total}', String(total))
                : `Showing ${from} to ${to} of ${total} entries`;
            if (!hasAnyFilter.value) return base;
            return `${base} (filtered: ${filteredItems.value.length})`;
        });

        const load = async () => {
            loading.value = true;
            try {
                const res = await window.axios.get('/admin/api/registration-queue', {
                    params: {
                        status: statusFilter.value,
                        q: (searchQuery.value || '').trim(),
                        page: page.value,
                        per_page: perPage.value,
                        lang: langValue(),
                    },
                });
                items.value = res.data?.data?.items || [];
                pagination.value = res.data?.data?.pagination || { page: 1, per_page: 20, total: 0 };
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                loading.value = false;
            }
        };

        const accept = async (id) => {
            busyIds.value = { ...busyIds.value, [id]: true };
            try {
                const res = await window.axios.post(`/admin/api/registration-queue/${id}/accept`, { lang: langValue() });
                addToast('success', res.data?.message || t('success'));
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [id]: false };
            }
        };

        const normalize = (v) => (v ?? '').toString().toLowerCase();

        const filteredItems = computed(() => {
            const list = Array.isArray(items.value) ? items.value : [];
            const f = colFilters.value || {};
            const idQ = normalize(f.id).trim();
            const dateQ = normalize(f.date).trim();
            const memberTypeQ = normalize(f.member_type).trim();
            const categoryQ = normalize(f.category).trim();
            const customerNameQ = normalize(f.customer_name).trim();
            const mobileQ = normalize(f.mobile).trim();
            const emailQ = normalize(f.email).trim();
            const districtQ = normalize(f.district).trim();
            const statusQ = (f.status || '').toString().trim().toLowerCase();

            if (!idQ && !dateQ && !memberTypeQ && !categoryQ && !customerNameQ && !mobileQ && !emailQ && !districtQ && !statusQ) return list;

            return list.filter((row) => {
                const id = normalize(row.RegistrationQueueID);
                const date = normalize(formatDateTime(row.RegistrationDate));
                const memberType = normalize(row.MemberType);
                const category = normalize(row.MembershipCategory);
                const customerName = normalize(row.CustomerName);
                const mobile = normalize(row.MobileNo);
                const email = normalize(row.Email);
                const district = normalize(row.District);
                const isPending = !!row.Status;

                if (idQ && !id.includes(idQ)) return false;
                if (dateQ && !date.includes(dateQ)) return false;
                if (memberTypeQ && !memberType.includes(memberTypeQ)) return false;
                if (categoryQ && !category.includes(categoryQ)) return false;
                if (customerNameQ && !customerName.includes(customerNameQ)) return false;
                if (mobileQ && !mobile.includes(mobileQ)) return false;
                if (emailQ && !email.includes(emailQ)) return false;
                if (districtQ && !district.includes(districtQ)) return false;
                if (statusQ === 'pending' && !isPending) return false;
                if (statusQ === 'processed' && isPending) return false;
                return true;
            });
        });

        const formatDateTime = (value) => {
            if (!value) return '-';
            try {
                return new Date(value).toLocaleString();
            } catch {
                return value;
            }
        };

        const addToast = (type, message) => {
            const id = Date.now() + Math.random();
            toasts.value.push({ id, type, message });
            setTimeout(() => removeToast(id), 4500);
        };

        const removeToast = (id) => {
            toasts.value = toasts.value.filter((t) => t.id !== id);
        };

        const debouncedSearch = () => {
            if (searchTimer) clearTimeout(searchTimer);
            searchTimer = setTimeout(async () => {
                page.value = 1;
                await load();
            }, 320);
        };

        const clearFilters = async () => {
            searchQuery.value = '';
            statusFilter.value = 'pending';
            colFilters.value = { id: '', date: '', member_type: '', category: '', customer_name: '', mobile: '', email: '', district: '', status: '' };
            page.value = 1;
            await load();
        };

        const goPage = async (p) => {
            const target = Math.min(Math.max(1, Number(p || 1)), totalPages.value);
            if (target === page.value) return;
            page.value = target;
            await load();
        };
        const nextPage = async () => {
            if (page.value >= totalPages.value) return;
            page.value += 1;
            await load();
        };
        const prevPage = async () => {
            if (page.value <= 1) return;
            page.value -= 1;
            await load();
        };
        const goFirst = async () => {
            if (page.value <= 1) return;
            page.value = 1;
            await load();
        };
        const goLast = async () => {
            if (page.value >= totalPages.value) return;
            page.value = totalPages.value;
            await load();
        };

        onMounted(load);

        return {
            loading,
            statusFilter,
            searchQuery,
            page,
            perPage,
            items,
            pagination,
            busyIds,
            toasts,
            t,
            columns,
            showColumns,
            visibleColumns,
            visibleColumnCount,
            showFilters,
            colFilters,
            hasAnyFilter,
            load,
            accept,
            filteredItems,
            formatDateTime,
            toggleFilters,
            toggleColumns,
            debouncedSearch,
            clearFilters,
            totalPages,
            pageButtons,
            showingText,
            goPage,
            nextPage,
            prevPage,
            goFirst,
            goLast,
            removeToast,
        };
    },
};
</script>

<style scoped>
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 16px;
}

.page-title {
    font-size: 22px;
    font-weight: 900;
    color: #0f172a;
    margin: 0;
}

.page-subtitle {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
}

.card {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 12px;
    padding: 12px;
}

.datatable-toolbar {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.length-select,
.status-select {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    padding: 0 10px;
    font-weight: 800;
    font-size: 12px;
}

.tool-btn {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 12px;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
    background: #ffffff;
    color: #0f172a;
}

.tool-btn-icon {
    width: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.tool-btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.tool-btn-light {
    background: #f1f5f9;
    color: #0f172a;
}

.tool-btn-success {
    background: #16a34a;
    color: #ffffff;
    border-color: #16a34a;
}

.tool-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.columns-wrap {
    position: relative;
}

.columns-dropdown {
    position: absolute;
    top: 36px;
    left: 0;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 10px;
    padding: 10px;
    min-width: 180px;
    z-index: 10;
    box-shadow: 0 10px 30px rgba(2, 6, 23, 0.12);
}

.columns-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 6px;
    font-weight: 800;
    font-size: 12px;
    color: #0f172a;
}

.search-box {
    position: relative;
    max-width: 520px;
    width: 100%;
}

.search-box svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-input {
    width: 100%;
    height: 36px;
    border-radius: 8px;
    border: 2px solid #2563eb;
    padding: 0 12px 0 42px;
    font-size: 13px;
    outline: none;
}

.table-container {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}

.data-table th {
    text-align: center;
    padding: 10px 8px;
    color: #ffffff;
    font-weight: 900;
    font-size: 12px;
    background: #0b2c3d;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    white-space: nowrap;
}

.th-input,
.th-select {
    width: 100%;
    height: 30px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.9);
    background: #ffffff;
    padding: 0 8px;
    font-weight: 700;
    font-size: 12px;
    outline: none;
}

.data-table td {
    padding: 9px 8px;
    border-top: 1px solid #e2e8f0;
    color: #0f172a;
    font-size: 13px;
    vertical-align: top;
    background: #ffffff;
}

.data-table tbody tr:nth-child(even) td {
    background: rgba(241, 245, 249, 0.55);
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
}

.tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
    border: 1px solid rgba(15, 23, 42, 0.12);
}

.tag-gray {
    background: #f1f5f9;
    color: #0f172a;
}

.tag-warning {
    background: rgba(245, 158, 11, 0.12);
    color: #92400e;
    border-color: rgba(245, 158, 11, 0.25);
}

.empty {
    text-align: center;
    padding: 24px;
    color: #64748b;
    font-weight: 700;
}

.pager {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding-top: 12px;
    flex-wrap: wrap;
}

.pager-left {
    color: #64748b;
    font-weight: 800;
    font-size: 12px;
}

.pager-right {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.pager-btn,
.pager-num {
    height: 30px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
}

.pager-num.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.pager-btn:disabled,
.pager-num:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.skeleton-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.skeleton-row {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.7), rgba(226, 232, 240, 0.25), rgba(226, 232, 240, 0.7));
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}
</style>
